<template>
  <section class="customer-users" aria-label="Customer users">
    <div class="users-heading">
      <div>
        <h3>Users</h3>
        <p>Manage user accounts and access for this customer.</p>
      </div>
      <b-button class="btn-green" @click="edit()">Add user</b-button>
    </div>
    <p v-if="loading && !loaded" role="status">Loading users…</p>
    <div v-else-if="error && !loaded" role="alert">
      <p>Could not load users.</p>
      <b-button variant="outline-secondary" @click="fetchUsers">Try again</b-button>
    </div>
    <p v-else-if="!users.length">No users added for this customer.</p>
    <div v-else class="users-table-wrap">
      <table class="users-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email address</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id || user.email">
            <td>{{ [user.firstName, user.lastName].filter(Boolean).join(' ') || '—' }}</td>
            <td>{{ user.email }}</td>
            <td>{{ getRoleLabel(user.role) }}</td>
            <td class="users-actions">
              <b-button variant="outline-secondary" :aria-label="`Edit ${user.email}`" @click="edit(user)">
                Edit
              </b-button>
              <b-button
                v-if="account.isStaff"
                variant="outline-secondary"
                :aria-label="`Log in as ${user.email}`"
                @click="login(user)"
              >
                Log in
              </b-button>
              <b-button
                variant="outline-danger"
                class="users-delete"
                :aria-label="`Delete ${user.email}`"
                @click="remove(user)"
              >
                Delete
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="error && loaded" role="alert" class="text-danger">
      Could not refresh users. The previous list is still shown.
    </p>
    <p v-if="actionError" role="alert" class="text-danger">Could not complete the action. Try again.</p>
  </section>
</template>
<script lang="ts">
import type { Account } from '@/types/Models/Account'
import { BButton } from 'bootstrap-vue-next'
import { ref, watch, defineComponent, type Ref } from 'vue'
import { USERS_CREATE_EDIT } from '@/const/ModalTypes'
import { getRoleLabel } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAuthStore } from '@/store/auth'
import { useVueNextModalStore } from '@/store/vueNextModal'
import AccountProxy from '@/proxies/AccountProxy'
import AccountTransformer from '@/transformers/AccountTransformer'

export default defineComponent({
  name: 'CustomerUsers',
  components: { BButton },
  props: {
    companyId: { type: String, required: true },
  },
  setup(props) {
    const account = useAccountStore()
    const auth = useAuthStore()
    const modals = useVueNextModalStore()
    const users: Ref<Account[]> = ref<Account[]>([])
    const loading: Ref<boolean> = ref(false)
    const loaded: Ref<boolean> = ref(false)
    const error: Ref<boolean> = ref(false)
    const actionError: Ref<boolean> = ref(false)
    let request = 0
    function fetchUsers(): Promise<void> {
      const current = ++request
      loading.value = true
      error.value = false
      return new AccountProxy({ companyId: props.companyId })
        .all()
        .then((response) => {
          if (current === request) {
            users.value = AccountTransformer.fetchCollection(response.data)
            loaded.value = true
          }
        })
        .catch(() => {
          if (current === request) error.value = true
        })
        .finally(() => {
          if (current === request) loading.value = false
        })
    }
    function edit(user?: Account): void {
      openModal(USERS_CREATE_EDIT, { companyId: props.companyId, user })
    }
    function login(user: Account): void {
      if (!user.id) return
      actionError.value = false
      auth.authAsCompany(props.companyId, user.id).catch(() => {
        actionError.value = true
      })
    }
    function remove(user: Account): void {
      if (!user.id) return
      openModal('confirmWrap', {
        title: 'Delete user?',
        message: `The user account ${user.email} will be deleted.`,
        okButtonText: 'Delete',
        onConfirm: (): Promise<void> => {
          actionError.value = false
          return new AccountProxy()
            .destroy(user.id!)
            .then(fetchUsers)
            .catch(() => {
              actionError.value = true
            })
        },
      })
    }
    watch(
      () => props.companyId,
      () => {
        loaded.value = false
        users.value = []
        fetchUsers()
      },
      { immediate: true },
    )
    watch(
      () => modals.isModalVisible(USERS_CREATE_EDIT),
      (visible, previous) => {
        if (previous && !visible) fetchUsers()
      },
    )
    return {
      account,
      auth,
      modals,
      users,
      loading,
      loaded,
      error,
      actionError,
      request,
      fetchUsers,
      edit,
      login,
      remove,
      getRoleLabel,
    }
  },
})
</script>
